import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { productPages } from '../data/pagesData';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import OptimizedImage from '../components/OptimizedImage';
import { ArrowRight, ChevronDown } from 'lucide-react';
import { logoCarousel, testimonialsData } from '../data/mockData';

const FadeText = ({ text }) => {
  const ref = useRef(null);
  const [vis, setVis] = useState(0);
  const words = text.split(' ');
  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => {
      if (e.isIntersecting) { let c = 0; const i = setInterval(() => { c++; setVis(c); if (c >= words.length) clearInterval(i); }, 40); obs.disconnect(); }
    }, { threshold: 0.3 });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, [words.length]);
  return (
    <div ref={ref} className="max-w-[1000px] mx-auto px-6 lg:px-10 py-20 lg:py-28">
      <p className="text-center text-[28px] lg:text-[38px] leading-[1.4] font-serif text-[#082900]">
        {words.map((w, i) => <span key={i} className="transition-opacity duration-300" style={{ opacity: i < vis ? 1 : 0.2 }}>{w}{' '}</span>)}
      </p>
    </div>
  );
};

const FeatureCard = ({ f, index }) => {
  const isEven = index % 2 === 0;
  const ref = useRef(null);
  const [show, setShow] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) { setShow(true); obs.disconnect(); } }, { threshold: 0.2 });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);
  return (
    <div ref={ref} className={`rounded-2xl overflow-hidden transition-all duration-700 ${show ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
      <div className={`flex flex-col lg:flex-row min-h-[400px] lg:min-h-[480px] ${!isEven ? 'lg:flex-row-reverse' : ''}`}>
        <div className="lg:w-1/2 bg-[#EAE5D9] p-8 lg:p-14 flex flex-col justify-center">
          <span className="text-[#082900] text-sm font-semibold tracking-wide uppercase font-inter mb-3">{f.label}</span>
          <h3 className="text-[#082900] text-[28px] lg:text-[36px] font-serif leading-tight mb-5">{f.title}</h3>
          <p className="text-[#082900]/70 text-base lg:text-lg font-inter leading-relaxed mb-6">{f.description}</p>
          <Link to="/demo" className="inline-flex items-center gap-2 text-[#082900] text-sm font-semibold font-inter hover:gap-3 transition-all duration-300 group">
            {f.linkText} <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
          </Link>
        </div>
        <div className="lg:w-1/2 bg-[#B8C4AD] p-8 lg:p-12 flex items-center justify-center">
          <OptimizedImage src={f.image} alt={f.title} className="max-w-full max-h-[360px] object-contain rounded-xl shadow-lg" />
        </div>
      </div>
    </div>
  );
};

const FAQ = ({ faqs }) => {
  const [open, setOpen] = useState(null);
  return (
    <div className="max-w-[800px] mx-auto px-6 lg:px-10 py-20 lg:py-28">
      <h2 className="text-[#082900] text-[36px] lg:text-[48px] font-serif mb-12">FAQs</h2>
      <div className="space-y-0">
        {faqs.map((faq, i) => (
          <div key={i} className="border-b border-[#082900]/10">
            <button onClick={() => setOpen(open === i ? null : i)} className="w-full flex items-center justify-between py-6 text-left">
              <span className="text-[#082900] text-lg font-medium font-inter pr-4">{faq.q}</span>
              <ChevronDown className={`w-5 h-5 text-[#082900] flex-shrink-0 transition-transform duration-300 ${open === i ? 'rotate-180' : ''}`} />
            </button>
            <div className={`overflow-hidden transition-all duration-400 ${open === i ? 'max-h-60 pb-6' : 'max-h-0'}`}>
              <p className="text-[#082900]/70 text-base font-inter leading-relaxed">{faq.a}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const ProductPage = () => {
  const { slug } = useParams();
  const page = productPages[slug];
  const [activeTab, setActiveTab] = useState(0);

  useEffect(() => { window.scrollTo(0, 0); }, [slug]);

  if (!page) return <div className="min-h-screen bg-[#F9FBF5] flex items-center justify-center"><p className="text-[#082900] text-2xl font-serif">Page not found</p></div>;

  return (
    <div className="bg-[#F9FBF5] min-h-screen">
      <Navbar />
      {/* Hero */}
      <section className="max-w-[1280px] mx-auto px-6 lg:px-10 pt-16 lg:pt-24 pb-8">
        <p className="text-[#082900] text-sm font-semibold tracking-wide uppercase font-inter mb-3">Product &nbsp;&nbsp; {page.label}</p>
        <h1 className="text-[#082900] text-[40px] sm:text-[56px] lg:text-[72px] font-serif leading-[1.1] max-w-3xl mb-6">{page.heroTitle}</h1>
        <p className="text-[#082900]/70 text-lg lg:text-xl font-inter max-w-2xl leading-relaxed mb-8">{page.heroDescription}</p>
        <Link to="/demo" className="inline-block px-8 py-4 bg-[#082900] text-white text-base font-medium rounded-full hover:bg-[#0A3900] transition-colors duration-300 font-inter">Request a Demo</Link>
      </section>
      <div className="max-w-[1280px] mx-auto px-6 lg:px-10 pb-16">
        <div className="rounded-2xl overflow-hidden"><img src={page.heroImage} alt={page.heroTitle} className="w-full h-auto object-cover" /></div>
      </div>
      {/* Logo bar */}
      <div className="pb-12">
        <p className="text-center text-[#082900] text-sm font-inter font-medium tracking-wide mb-8">Trusted by Innovative Talent Teams at</p>
        <div className="relative overflow-hidden"><div className="logo-carousel-track flex items-center gap-16">
          {[...logoCarousel.logos, ...logoCarousel.logos, ...logoCarousel.logos].map((l, i) => <img key={i} src={l.url} alt={l.name} className="h-8 md:h-10 w-auto object-contain flex-shrink-0 opacity-80" loading="lazy" />)}
        </div></div>
      </div>
      {/* Feature text */}
      <FadeText text={page.featureText} />
      {/* Feature cards */}
      <div className="max-w-[1280px] mx-auto px-6 lg:px-10 pb-20">
        <div className="flex flex-col gap-8">{page.features.map((f, i) => <FeatureCard key={i} f={f} index={i} />)}</div>
      </div>
      {/* Accelerate */}
      <section className="max-w-[1280px] mx-auto px-6 lg:px-10 py-20">
        <span className="text-[#082900] text-sm font-semibold tracking-wide uppercase font-inter">Accelerate Hiring</span>
        <div className="flex flex-col lg:flex-row gap-8 lg:gap-16 mt-10">
          <div className="lg:w-1/2"><div className="rounded-2xl overflow-hidden aspect-[4/5] lg:aspect-[3/4]"><img src={page.accelerate.tabs[activeTab].image} alt="" className="w-full h-full object-cover transition-opacity duration-500" /></div></div>
          <div className="lg:w-1/2 flex flex-col justify-center">
            {page.accelerate.tabs.map((tab, i) => (
              <div key={i} className={`border-l-2 pl-6 lg:pl-8 cursor-pointer transition-all duration-300 ${activeTab === i ? 'border-[#082900]' : 'border-[#082900]/10 hover:border-[#082900]/30'}`} onClick={() => setActiveTab(i)}>
                <div className="py-6">
                  <h3 className={`text-[24px] lg:text-[30px] font-serif transition-colors ${activeTab === i ? 'text-[#082900]' : 'text-[#082900]/40'}`}>{tab.title}</h3>
                  <div className={`overflow-hidden transition-all duration-500 ${activeTab === i ? 'max-h-40 opacity-100 mt-3' : 'max-h-0 opacity-0'}`}>
                    <p className="text-[#082900]/70 text-base font-inter leading-relaxed">{tab.description}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
      {/* Testimonials */}
      <section className="max-w-[1280px] mx-auto px-6 lg:px-10 py-20">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {testimonialsData.map((t, i) => (
            <div key={i} className="bg-[#EAE5D9] rounded-2xl p-6 lg:p-8">
              <div className="w-14 h-14 rounded-full overflow-hidden mb-5"><img src={t.avatar} alt={t.name} className="w-full h-full object-cover" /></div>
              <p className="text-[#082900] text-base font-serif font-bold leading-relaxed mb-6">{t.quote}</p>
              <p className="text-[#082900] text-sm font-semibold font-inter">{t.name}</p>
              <p className="text-[#082900]/60 text-sm font-inter">{t.title}</p>
            </div>
          ))}
        </div>
      </section>
      {/* FAQ */}
      <FAQ faqs={page.faqs} />
      <Footer />
    </div>
  );
};

export default ProductPage;
